import {useCallback, useContext, useMemo} from 'react';

import {useBrands} from '../../../../../provider/brands/brand-hook';
import {BrandType} from '../../../../../provider/brands/brand-type';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {UseHookType} from '../../../../../service/api-hook/api-hook-type';
import {useCompetitors} from '../../../../../service/reivew-analysis/competitors/competitors-hook';
import {CompetitorType} from '../../../../../service/reivew-analysis/competitors/competitors-type';
import {useReviewsAnalysisFilter} from '../../reviews-analysis-hook';
import {ReviewsAnalysisFilterEnum} from '../../reviews-analysis-type';

import {ReviewAnalysisBrandHookResultType} from './dashboard-type';

export function useReviewBrands(): UseHookType<ReviewAnalysisBrandHookResultType> & {
    result: ReviewAnalysisBrandHookResultType;
} {
    const {state: mainFilterState} = useContext<MainFilterContextType>(MainFilterContext);
    const {filter} = useReviewsAnalysisFilter();
    const competitorBrandId: number | null = filter[ReviewsAnalysisFilterEnum.Competitor];

    const {brands: mainFilterBrands} = mainFilterState;

    const {brands, isLoading: isCustomerBrandsInProgress} = useBrands();

    const {
        result: competitorsResult,
        isInProgress: isCompetitorsInProgress,
        processError: competitorsProcessError,
        reset: competitorsReset,
    } = useCompetitors();

    const reset = useCallback(() => {
        competitorsReset();
    }, [competitorsReset]);

    const result: ReviewAnalysisBrandHookResultType =
        useMemo<ReviewAnalysisBrandHookResultType>((): ReviewAnalysisBrandHookResultType => {
            if (brands && competitorsResult) {
                const competitorBrands: Array<CompetitorType> | null = competitorsResult;

                return {
                    nameMap: {
                        ...Object.fromEntries(brands.map((brand: BrandType) => [brand.id, brand.name])),
                        ...Object.fromEntries(competitorBrands.map((brand: CompetitorType) => [brand.id, brand.name])),
                    },
                    customerBrands: brands,
                    competitorBrands,
                    selectedCustomerBrandIds:
                        mainFilterBrands.length > 0
                            ? mainFilterBrands.map((brand) => Number(brand.id))
                            : brands.map((brand: BrandType) => brand.id),
                    selectedCompetitorBrandIds: competitorBrandId
                        ? [competitorBrandId]
                        : competitorBrands.map((brand: CompetitorType) => brand.id),
                };
            }

            return {
                nameMap: {},
                customerBrands: [],
                competitorBrands: [],
                selectedCustomerBrandIds: [],
                selectedCompetitorBrandIds: [],
            };
        }, [brands, competitorsResult, mainFilterBrands, competitorBrandId]);

    return {
        result,
        reset,
        isInProgress: isCustomerBrandsInProgress || isCompetitorsInProgress,
        processError: competitorsProcessError,
    };
}
